import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Area } from '../../core/constants/area';
import { CreditRiskDebtorResponse } from './models/responses/CreditRiskDebtorResponse';
import { DebtorListResponse } from './models/responses/DebtorListResponse';
import { SetGradingAuditModel } from './models/SetGradingAuditModel';
import { SetCreditRiskRequestModel } from './models/SetCreditRiskRequestModel';
import { FileItem } from 'ng2-file-upload';
import { SetGradeModel } from '../debtors/models/SetGradeModel';
import { SetApprovalOverrideModel } from './models/SetApprovalOverrideModel';

@Injectable()
export class InternalScoringService {
	constructor(public httpClient: HttpClient) {}

	getCreditRiskRequestCount(isInternal: boolean, isUpdate: boolean) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/CreditRisk/Request/Count?isInternal=${isInternal}&isUpdate=${isUpdate}`;
		return this.httpClient.get<number>(url);
	}
	getCreditRiskGrading(clientGrade: string, debtorGrade: string) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/CreditRisk/Grading?clientGrade=${clientGrade}&debtorGrade=${debtorGrade}`;
		return this.httpClient.get<any[]>(url);
	}
	getClientGrade(clientId: number) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/ClientGrade?clientId=${clientId}`;
		return this.httpClient.get<any[]>(url);
	}
	getDebtorGrade(debtorId: number) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/DebtorGrade?debtorId=${debtorId}`;
		return this.httpClient.get<any[]>(url);
	}
	getGradingAudit(clientKey: number, debtorKey: number) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/CreditRisk/GradingAudit?clientKey=${clientKey}&debtorKey=${debtorKey}`;
		return this.httpClient.get<any[]>(url);
	}
	getDebtor(clientGrade: string, debtorKey: number) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/Debtor?clientGrade=${clientGrade}&debtorKey=${debtorKey}`;
		return this.httpClient.get<CreditRiskDebtorResponse>(url);
	}

	getClientGradeDropdown() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/ClientGrade/Dropdown`;
		return this.httpClient.get<any[]>(url);
	}
	getDebtorGradeDropdown() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/DebtorGrade/Dropdown`;
		return this.httpClient.get<any[]>(url);
	}

	getActionCodeList() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/ActionCode/List`;
		return this.httpClient.get<any[]>(url);
	}
	getDebtorGradeList(clientKey?: number, sourceSystemId?: number) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/DebtorGrade/List`;
		let params = new HttpParams();
		if (clientKey != null) {
			params = params.set('clientKey', clientKey.toString());
		}
		if (sourceSystemId != null) {
			params = params.set('sourceSystemId', sourceSystemId.toString());
		}
		return this.httpClient.get<DebtorListResponse[]>(url, { params });
	}
	getGradingAuditList(startDate?: Date, endDate?: Date) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/GradingAudit/List`;
		
		const params: HttpParams = new HttpParams()
			.set('startDate', startDate ? startDate.toISOString() : '')
			.set('endDate', endDate ? endDate.toISOString() : '');
		
		return this.httpClient.get<any[]>(url, { params });
	}
	getCreditRiskRequestList(isInternal: boolean) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/CreditRisk/Request/List?isInternal=${isInternal}`;
		return this.httpClient.get<any[]>(url);
	}
	getCreditRiskResolvedList() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/CreditRisk/Resolved/List`;
		return this.httpClient.get<any[]>(url);
	}
	getCreditRiskLevelList() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/CreditRisk/Level/List`;
		return this.httpClient.get<any[]>(url);
	}
	getCreditRiskGradingMatrix() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/CreditRisk/Grading/Matrix`;
		return this.httpClient.get<any[]>(url);
	}
	getApprovalOverrideList() {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/ApprovalOverride/List`;
		return this.httpClient.get<any[]>(url);
	}

	archiveCreditRiskRequest(requestId: number) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/CreditRisk/Request?requestId=${requestId}`;
		return this.httpClient.patch<any[]>(url, {});
	}
	setGradingAudit(model: SetGradingAuditModel) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/GradingAudit`;
		return this.httpClient.post<any[]>(url, model);
	}
	setApprovalOverride(model: SetApprovalOverrideModel) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/ApprovalOverride`;
		return this.httpClient.post<any[]>(url, model);
	}
	setCreditRiskRequest(model: SetCreditRiskRequestModel) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/CreditRisk/Request`;
		return this.httpClient.post<any[]>(url, model);
	}
	setCreditRiskRequestWithDocuments(
		model: SetCreditRiskRequestModel,
		uploadedFiles: FileItem[]
	) {
		const url = `${environment.dotNetApiUrl}/${Area.factoring}/InternalScoring/CreditRisk/RequestWithDocuments`;

		const headers = new HttpHeaders();
		headers.set('Content-Type', null);
		headers.set('Accept', 'multipart/form-data');
		const params = new HttpParams();

		const formData = new FormData();

		uploadedFiles.forEach((uploadedFile, index) => {
			const file = uploadedFile.file;
			const blob = new Blob([file.rawFile], { type: file.type });
			formData.append(`${index}-${file.name}`, blob, `${index}-${file.name}`);
		});
		formData.append('model', JSON.stringify(model));

		return this.httpClient.post<any>(url, formData, {
			params,
			headers,
			reportProgress: true,
			observe: 'events',
		});
	}
}



// WEBPACK FOOTER //
// ./src/app/portal/internal-scoring/internal-scoring.service.ts